/**
 * Documents module contract (design B11 "File library"): kinds, linkable records, request schemas
 * and response types shared by the server and the web app. Query params are snake_case.
 */
import { z } from 'zod';
import { pluralRu } from '../ru';

// ------------------------------------------------------------------ kinds

/** Same values as server/src/lib/files.ts DOCUMENT_KINDS (documents rules.test.ts asserts they match). */
export const DOCUMENT_KINDS = [
  'contract',
  'amendment',
  'nda',
  'invoice_pdf',
  'client_invoice',
  'statement',
  'other',
] as const;
export type DocumentKind = (typeof DOCUMENT_KINDS)[number];

/** Design "Type" labels (Documents.dc.html TYPES) plus «Счёт клиента» for client-issued PDFs. */
const DOCUMENT_KIND_LABELS: Readonly<Record<DocumentKind, string>> = {
  contract: 'Договор',
  amendment: 'Доп. соглашение',
  nda: 'NDA',
  invoice_pdf: 'PDF счёта',
  client_invoice: 'Счёт клиента',
  statement: 'Выписка',
  other: 'Другое',
};

/** "contract" → «Договор». */
export function documentKindLabel(kind: DocumentKind): string {
  return DOCUMENT_KIND_LABELS[kind];
}

/** Kinds the invoices module writes; linked to an invoice they are managed there, not here. */
export const INVOICE_MANAGED_KINDS: readonly DocumentKind[] = ['invoice_pdf', 'client_invoice'];

// ------------------------------------------------------------------ linked records

/** Records a user may link a file to from the Documents preview ("Linked record"). */
export const LINKABLE_ENTITY_TYPES = [
  'client',
  'person',
  'issuer',
  'contract',
  'contract_amendment',
  'project',
  'expense',
] as const;
export type LinkableEntityType = (typeof LINKABLE_ENTITY_TYPES)[number];

/** Every `documents.entity_type` value; `invoice` is written only by the invoices module. */
export const DOCUMENT_ENTITY_TYPES = [...LINKABLE_ENTITY_TYPES, 'invoice'] as const;
export type DocumentEntityType = (typeof DOCUMENT_ENTITY_TYPES)[number];

const DOCUMENT_ENTITY_LABELS: Readonly<Record<DocumentEntityType, string>> = {
  client: 'Клиент',
  person: 'Сотрудник',
  issuer: 'Исполнитель',
  contract: 'Договор',
  contract_amendment: 'Доп. соглашение',
  project: 'Проект',
  expense: 'Расход',
  invoice: 'Счёт',
};

/** `linkKind` shown next to a link: «Клиент». */
export function documentEntityLabel(type: DocumentEntityType): string {
  return DOCUMENT_ENTITY_LABELS[type];
}

/** «Не связан» (link chip, "Linked record" select, API `linkLabel`). */
export const NOT_LINKED_LABEL = 'Не связан';

// ------------------------------------------------------------------ copy (design toasts)

/** Refusal texts (design toasts). */
export const INVOICE_LINK_REFUSAL = 'PDF счетов связаны со своими счетами';
export const INVOICE_DELETE_REFUSAL = 'PDF счёта удаляется только отменой счёта';

/** «Загружен 1 файл» / «Загружено 3 файла» (design D:100). */
export const uploadedText = (count: number): string =>
  pluralRu(count, `Загружен ${count} файл`, `Загружено ${count} файла`, `Загружено ${count} файлов`);
/** Bulk bar delete: «Удалён 1 файл» / «Удалено 2 файла» (design D:122). */
export const deletedFilesText = (count: number): string =>
  pluralRu(count, `Удалён ${count} файл`, `Удалено ${count} файла`, `Удалено ${count} файлов`);
/** Drawer delete: «Удалён файл «NDA_Kirsan_Hart.pdf»» (design D:133). File names stay as uploaded. */
export const deletedFileText = (filename: string): string => `Удалён файл «${filename}»`;
/** «2 файла» (restore toasts). */
export const fileCountText = (count: number): string =>
  `${count} ${pluralRu(count, 'файл', 'файла', 'файлов')}`;

// ------------------------------------------------------------------ pure rules

const STATEMENT_RE = /statement|выписк/i;
const NDA_RE = /nda/i;

/**
 * Upload auto-type, exactly the design's rule (D:101): /statement/i → statement, /nda/i → nda,
 * otherwise other. Like the design it also matches "nda" inside words ("Calendar.pdf"); the
 * preview drawer's Type select corrects such guesses.
 */
export function guessDocumentKind(filename: string): DocumentKind {
  if (STATEMENT_RE.test(filename)) return 'statement';
  if (NDA_RE.test(filename)) return 'nda';
  return 'other';
}

const BYTES_PER_KB = 1024;

/** Design size unit: whole KB, at least 1 (D:101). */
export function documentSizeKb(bytes: number): number {
  return Math.max(1, Math.round(bytes / BYTES_PER_KB));
}

/** Whole KB → megabytes with one decimal: "3,4" (no grouping, like the design). */
function megabytesText(kb: number): string {
  return (kb / BYTES_PER_KB).toFixed(1).replace('.', ',');
}

/** Table "Size": «48 КБ», or «1,2 МБ» from 1024 KB (D:112). */
export function formatDocumentSize(bytes: number): string {
  const kb = documentSizeKb(bytes);
  return kb >= BYTES_PER_KB ? `${megabytesText(kb)} МБ` : `${kb} КБ`;
}

/**
 * Header summary (D:118): «12 файлов · 3,4 МБ · PDF счетов добавляются автоматически при
 * выставлении счетов». Like the design, MB = the sum of the per-file whole-KB sizes / 1024.
 */
export function librarySummaryText(summary: Pick<DocumentSummary, 'count' | 'totalKb'>): string {
  return `${fileCountText(summary.count)} · ${megabytesText(summary.totalKb)} МБ · PDF счетов добавляются автоматически при выставлении счетов`;
}

// ------------------------------------------------------------------ request schemas

const blankAsOmitted = (value: unknown) => (value === '' ? undefined : value);
const id = z.string().trim().min(1).max(64);

/** GET /documents?kind=&q=&entity_type=&entity_id= */
export const documentListQuerySchema = z.object({
  kind: z.preprocess(blankAsOmitted, z.enum([...DOCUMENT_KINDS, 'all'] as const).optional()),
  q: z.string().trim().max(200).optional(),
  entity_type: z.preprocess(blankAsOmitted, z.enum(DOCUMENT_ENTITY_TYPES).optional()),
  entity_id: z.preprocess(blankAsOmitted, id.optional()),
});
export type DocumentListQuery = z.output<typeof documentListQuerySchema>;

const linkPairIsComplete = (value: { entityType?: unknown; entityId?: unknown }) =>
  (value.entityType === undefined) === (value.entityId === undefined) &&
  (value.entityType === null) === (value.entityId === null);
const LINK_PAIR_MESSAGE = 'Передайте entityType и entityId вместе';

/** Text fields of the multipart POST /documents (all optional; '' means omitted). */
export const documentUploadFieldsSchema = z
  .object({
    kind: z.preprocess(blankAsOmitted, z.enum(DOCUMENT_KINDS).optional()),
    entityType: z.preprocess(blankAsOmitted, z.enum(LINKABLE_ENTITY_TYPES).optional()),
    entityId: z.preprocess(blankAsOmitted, id.optional()),
  })
  .refine(linkPairIsComplete, { message: LINK_PAIR_MESSAGE, path: ['entityId'] });
export type DocumentUploadFields = z.output<typeof documentUploadFieldsSchema>;

/** PATCH /documents/:id — retype and/or relink; `null` pair unlinks. */
export const documentUpdateSchema = z
  .strictObject({
    kind: z.enum(DOCUMENT_KINDS).optional(),
    entityType: z.enum(LINKABLE_ENTITY_TYPES).nullable().optional(),
    entityId: id.nullable().optional(),
  })
  .refine(linkPairIsComplete, { message: LINK_PAIR_MESSAGE, path: ['entityId'] })
  .refine((body) => Object.keys(body).length > 0, 'Нет изменений для сохранения');
export type DocumentUpdateInput = z.input<typeof documentUpdateSchema>;
export type DocumentUpdate = z.output<typeof documentUpdateSchema>;

export const MAX_DOCUMENT_IDS = 500;

/** POST /documents/delete and POST /documents/zip */
export const documentIdsSchema = z.strictObject({
  ids: z
    .array(id)
    .min(1, 'Выберите хотя бы один файл')
    .max(MAX_DOCUMENT_IDS)
    .transform((ids) => [...new Set(ids)]),
});

// ------------------------------------------------------------------ response types

export interface DocumentItem {
  readonly id: string;
  readonly filename: string;
  readonly kind: DocumentKind;
  readonly kindLabel: string;
  readonly entityType: string | null;
  readonly entityId: string | null;
  /** Resolved record name, or "Not linked". */
  readonly linkLabel: string;
  /** "Client", "Person", "Invoice", ...; null when not linked. */
  readonly linkKind: string | null;
  /** Secondary context: the client of an invoice or project, the party of a contract. */
  readonly linkDetail: string | null;
  readonly uploadedAt: string;
  /** Bytes. */
  readonly size: number;
  readonly mime: string;
  /** Invoice PDFs and client invoices of an invoice: read-only here (relink/delete refused). */
  readonly derived: boolean;
  /** The invoice behind a derived file ("Open record"). */
  readonly invoiceId: string | null;
  /** Derived file of a cancelled, reopened or superseded invoice: listed but muted. */
  readonly muted: boolean;
  /** PDFs and images: GET /documents/:id/preview renders inline. */
  readonly previewable: boolean;
}

export interface DocumentSummary {
  readonly count: number;
  readonly totalBytes: number;
  /** Σ documentSizeKb(size): the design's summary unit. */
  readonly totalKb: number;
  readonly perKind: Readonly<Record<DocumentKind, number>>;
}

/** GET /documents. `summary` ignores `kind` and `q` (the design's counts), honours entity filters. */
export interface DocumentListResponse {
  readonly items: readonly DocumentItem[];
  readonly summary: DocumentSummary;
}

export interface DocumentLinkOption {
  readonly entityType: LinkableEntityType;
  readonly entityId: string;
  readonly label: string;
  readonly detail: string | null;
}

export interface DocumentLinkOptionsResponse {
  readonly items: readonly DocumentLinkOption[];
}

export interface DocumentUploadResponse {
  readonly items: readonly DocumentItem[];
  readonly undoToken: string;
}

export interface DocumentMutationResponse {
  readonly item: DocumentItem;
  /** Absent when the PATCH changed nothing. */
  readonly undoToken?: string;
}

export interface DocumentDeleteResponse {
  readonly ok: true;
  readonly undoToken: string;
}

export interface DocumentBulkDeleteResponse extends DocumentDeleteResponse {
  readonly deletedIds: readonly string[];
  /** Invoice-managed files skipped silently (design bulk delete). */
  readonly skippedIds: readonly string[];
}
